//
//  tourStepText.tsx
//
//  Copyright:
//    NewtonGreen Technologies Pty. Ltd.
//    Level 4, 175 Scott Street
//    Newcastle, NSW, 2300
//    Australia
//
//    E-mail: support@newtongreen.com
//    Tel: (02) 4925 5288
//
//    All Rights Reserved.
//
//  Function of this module:
//    This module applies the maintained title and text of a page's tour steps to its hard-coded
//    steps.
//

import type { HelpTourStep as StoredTourStep } from "src/api/Tmd.dtos";
import RichTextContent from "src/components/common/RichTextContent";

import type { HelpTourStep } from "./helpTourTargets";

/** The key a step's stored text is saved under: its own key, or its target id. */
export const tourStepKey = (step: HelpTourStep) => step.key ?? step.id;

/** Escapes text for use inside HTML. */
const escapeHtml = (text: string) =>
    text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/** A step's default title as text, when it has one. */
export const defaultTourStepTitle = (step: HelpTourStep) =>
    typeof step.title === "string" ? step.title : undefined;

/**
 * A step's default wording as rich text, in the form the seed migrations store it; empty when its
 * default is not plain text.
 */
export const defaultTourStepContent = (step: HelpTourStep) =>
    typeof step.content === "string" ? `<p>${escapeHtml(step.content)}</p>` : "";

/**
 * Reports whether a step's stored text differs from its default wording.
 *
 * Postconditions: A blank stored title counts as the default title. Hidden steps are judged on
 * their text alone; whether a step is shown is reported separately.
 */
export const isTourStepChanged = (step: HelpTourStep, stored: StoredTourStep) => {
    const title = stored.title?.trim();
    return (
        (!!title && title !== defaultTourStepTitle(step)) ||
        (stored.content ?? "").trim() !== defaultTourStepContent(step)
    );
};

/**
 * Replaces each step's default wording with the text maintained for it.
 *
 * Postconditions: A step with an active stored row takes its text, rendered as sanitised rich
 * text, and its title when one was entered. A step whose stored row is inactive is dropped. A step
 * without a stored row keeps the wording written in its page guide. Stored rows whose key is not in
 * the tour are ignored. The steps' targets, order, chapters and permissions are unchanged.
 */
export const applyStoredTourText = (
    steps: readonly HelpTourStep[],
    stored: readonly StoredTourStep[] | undefined
): HelpTourStep[] => {
    if (!stored?.length) {
        return [...steps];
    }

    const byKey = new Map(stored.map(row => [row.stepKey, row]));
    return steps.flatMap<HelpTourStep>(step => {
        const row = byKey.get(tourStepKey(step));
        if (!row) {
            return [step];
        }
        if (row.active === false) {
            return [];
        }

        const merged: HelpTourStep = {
            ...step,
            title: row.title?.trim() ? row.title : step.title,
            content: row.content ? (
                <RichTextContent
                    content={row.content}
                    fz="sm"
                />
            ) : (
                step.content
            )
        };
        return [merged];
    });
};
